import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaArrowRight,
    FaCalendarAlt,
    FaFilter,
    FaSearch,
    FaBuilding,
    FaClipboardCheck,
    FaExclamationTriangle,
    FaTable,
    FaUserShield,
    FaChartBar,
    FaTimes,
} from "react-icons/fa";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";
import "../PriceSurvyReports/PriceSurveyMatrixReportPage.css";
import "./PatrolActivityMatrixReportPage.css";

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

const formatDateLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value ?? "");
    return date.toLocaleDateString("ar-SY", {
        weekday: "short",
        day: "numeric",
        month: "short",
    });
};

const emptyTotals = () => ({
    establishments: 0,
    inspectionVisits: 0,
    violationReports: 0,
    active: 0,
});

const addTotals = (acc, totals) => {
    if (!totals) return acc;
    acc.establishments += totals.establishments || 0;
    acc.inspectionVisits += totals.inspectionVisits || 0;
    acc.violationReports += totals.violationReports || 0;
    acc.active += 1;
    return acc;
};

const shortName = (name = "") => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 2) return name;
    return `${parts[0]} ${parts[parts.length - 1]}`;
};

const METRIC_OPTIONS = [
    { key: "establishments", label: "منشآت", icon: FaBuilding, color: "#2563eb", dataKey: "establishments" },
    { key: "inspectionVisits", label: "زيارات", icon: FaClipboardCheck, color: "#16a34a", dataKey: "inspectionVisits" },
    { key: "violationReports", label: "ضبوط", icon: FaExclamationTriangle, color: "#dc2626", dataKey: "violationReports" },
    { key: "all", label: "الكل", icon: FaChartBar },
];

const ChartYTick = ({ x, y, payload }) => (
    <text
        x={x - 8}
        y={y}
        dy={4}
        textAnchor="end"
        fill="#0f172a"
        fontSize={13}
        fontWeight={700}
    >
        {payload?.value}
    </text>
);

const CellTotals = ({ totals, compact = false }) => {
    if (!totals) return <span className="text-muted">—</span>;
    const est = totals.establishments ?? 0;
    const visits = totals.inspectionVisits ?? 0;
    const viol = totals.violationReports ?? 0;

    if (compact) {
        return (
            <div
                className="patrol-matrix-cell patrol-matrix-cell--compact"
                dir="ltr"
                title={`فعاليات ${est} · زيارات ${visits} · ضبوط ${viol}`}
            >
                <span className="pm-n pm-n--est">{est}</span>
                <span className="pm-sep">/</span>
                <span className="pm-n pm-n--visit">{visits}</span>
                <span className="pm-sep">/</span>
                <span className="pm-n pm-n--viol">{viol}</span>
            </div>
        );
    }

    return (
        <div className="patrol-matrix-cell" dir="rtl">
            <div className="patrol-matrix-cell__item" title="فعاليات">
                <FaBuilding className="patrol-matrix-cell__ico text-primary" />
                <span>{est}</span>
            </div>
            <div className="patrol-matrix-cell__item" title="زيارات">
                <FaClipboardCheck className="patrol-matrix-cell__ico text-success" />
                <span>{visits}</span>
            </div>
            <div className="patrol-matrix-cell__item" title="ضبوط">
                <FaExclamationTriangle className="patrol-matrix-cell__ico text-danger" />
                <span>{viol}</span>
            </div>
        </div>
    );
};

const DayChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="pm-chart-tooltip" dir="rtl">
            <div className="pm-chart-tooltip__title">{label}</div>
            {payload.map((item) => (
                <div key={item.dataKey} className="pm-chart-tooltip__row">
                    <span style={{ color: item.color || item.fill }}>{item.name}</span>
                    <strong>{item.value ?? 0}</strong>
                </div>
            ))}
        </div>
    );
};

export default function PatrolActivityMatrixReportPage() {
    const navigate = useNavigate();
    const { backEnd_request, getUserConstraints } = BackMethods();
    const { userGovernorate, isSuperAdmin } = getUserConstraints();

    const [locationsData, setLocationsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [matrixData, setMatrixData] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [dayChart, setDayChart] = useState(null);
    const [chartMetric, setChartMetric] = useState("establishments");
    const [filters, setFilters] = useState({
        from: daysAgoIso(7),
        to: todayIso(),
        governorate: isSuperAdmin ? "" : userGovernorate || "",
    });

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await backEnd_request.get("/Locations?includeInactive=false", {
                    params: { size: 10000 },
                });
                setLocationsData(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchLocations();
    }, [backEnd_request]);

    const availableGovernorates = useMemo(
        () => [...new Set(locationsData.map((l) => l.governorate).filter(Boolean))],
        [locationsData]
    );

    const matrixView = useMemo(() => {
        if (!matrixData?.days || !matrixData?.rows) return null;
        const users = matrixData.rows.map((row) => ({
            userId: row.user?.userId,
            name: row.user?.fullName || row.user?.username || "—",
            governorate: row.user?.governorate || "",
        }));

        const dayRows = matrixData.days.map((day, dayIndex) => {
            const cells = matrixData.rows.map((row) => row.cells?.[dayIndex] ?? null);
            const dayTotals = cells.reduce((acc, cell) => addTotals(acc, cell?.totals), emptyTotals());
            return { day, cells, dayTotals };
        });

        const userTotals = users.map((_, userIndex) =>
            dayRows.reduce((acc, row) => addTotals(acc, row.cells[userIndex]?.totals), emptyTotals())
        );

        const grand = userTotals.reduce(
            (acc, t) => {
                acc.establishments += t.establishments;
                acc.inspectionVisits += t.inspectionVisits;
                acc.violationReports += t.violationReports;
                acc.active += t.active;
                return acc;
            },
            emptyTotals()
        );

        return { users, dayRows, userTotals, grand };
    }, [matrixData]);

    const chartData = useMemo(() => {
        if (!dayChart || !matrixView) return [];
        const valueKey = chartMetric === "all" ? "total" : chartMetric;
        return dayChart.cells
            .map((cell, idx) => {
                const user = matrixView.users[idx];
                const totals = cell?.totals;
                if (!totals) return null;
                return {
                    name: shortName(user.name),
                    fullName: user.name,
                    establishments: totals.establishments || 0,
                    inspectionVisits: totals.inspectionVisits || 0,
                    violationReports: totals.violationReports || 0,
                    total:
                        (totals.establishments || 0) +
                        (totals.inspectionVisits || 0) +
                        (totals.violationReports || 0),
                };
            })
            .filter(Boolean)
            .filter((row) => (row[valueKey] || 0) > 0)
            .sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
    }, [dayChart, matrixView, chartMetric]);

    const chartStats = useMemo(() => {
        if (!chartData.length) return null;
        const key = chartMetric === "all" ? "total" : chartMetric;
        const values = chartData.map((d) => d[key] || 0);
        const max = Math.max(...values);
        const min = Math.min(...values);
        const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
        const maxItem = chartData.find((d) => (d[key] || 0) === max);
        const minItem = chartData.find((d) => (d[key] || 0) === min);
        return {
            max,
            min,
            avg: Math.round(avg * 10) / 10,
            maxName: maxItem?.fullName || maxItem?.name || "—",
            minName: minItem?.fullName || minItem?.name || "—",
        };
    }, [chartData, chartMetric]);

    const openDayChart = (row) => {
        setChartMetric("establishments");
        setDayChart(row);
    };

    const fetchReport = async () => {
        if (!filters.from || !filters.to) {
            setError("يجب تحديد من تاريخ وإلى تاريخ");
            return;
        }
        if (!filters.governorate) {
            setError("يجب اختيار المحافظة قبل عرض التقرير");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params = {
                kind: "matrix",
                from: filters.from,
                to: filters.to,
                governorate: filters.governorate,
            };

            const res = await backEnd_request.get("/admin/reports/geo", { params });
            setMatrixData(res.data || null);
            setFiltersOpen(false);
        } catch (err) {
            console.error(err);
            setMatrixData(null);
            setError(err.response?.data?.message || err.response?.data || "تعذر جلب تقرير نشاط الدوريات");
        } finally {
            setLoading(false);
        }
    };

    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const userCount = matrixView?.users?.length || 0;
    const useScrollLayout = userCount > 4;
    const useCompactCells = userCount > 8;
    const chartHeight = Math.max(320, Math.min(560, 80 + chartData.length * 42));
    const selectedMetric = METRIC_OPTIONS.find((m) => m.key === chartMetric);

    return (
        <div
            className={`container-fluid px-3 px-md-4 py-4 ps-matrix-page ${
                !filtersOpen && matrixData ? "ps-matrix-filters-collapsed" : ""
            }`}
            dir="rtl"
        >
            <div className="ps-matrix-header mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div>
                    <h3 className="mb-1 d-flex align-items-center gap-2">
                        <FaTable /> نشاط الدوريات
                    </h3>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        <span className="meta-badge">
                            <FaCalendarAlt className="ms-1" />
                            {filters.from} → {filters.to}
                        </span>
                        {filters.governorate && (
                            <span className="meta-badge">{filters.governorate}</span>
                        )}
                        {matrixView && (
                            <span className="meta-badge">
                                {matrixView.users.length} مستخدم · {matrixView.dayRows.length} يوم
                            </span>
                        )}
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="btn btn-light btn-sm rounded-pill fw-bold"
                        onClick={() => setFiltersOpen((v) => !v)}
                    >
                        <FaFilter className="ms-1" />
                        {filtersOpen ? "إخفاء الفلاتر" : "إظهار الفلاتر"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm rounded-pill fw-bold"
                        onClick={() => navigate("/admin/patrol-rosters")}
                    >
                        <FaArrowRight className="ms-1" /> العودة للدوريات
                    </button>
                </div>
            </div>

            {!filtersOpen && matrixData && (
                <div className="ps-matrix-filters-summary mb-3 d-flex flex-wrap gap-2 align-items-center">
                    <span className="badge bg-primary-subtle text-primary">من {filters.from}</span>
                    <span className="badge bg-primary-subtle text-primary">إلى {filters.to}</span>
                    {filters.governorate && (
                        <span className="badge bg-secondary-subtle text-secondary">{filters.governorate}</span>
                    )}
                </div>
            )}

            {filtersOpen && (
                <div className="ps-matrix-filters p-4 mb-3">
                    <div className="row g-3 align-items-end">
                        <div className="col-6 col-md-3">
                            <label>من تاريخ</label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.from}
                                onChange={(e) => updateFilter("from", e.target.value)}
                            />
                        </div>
                        <div className="col-6 col-md-3">
                            <label>إلى تاريخ</label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.to}
                                onChange={(e) => updateFilter("to", e.target.value)}
                            />
                        </div>
                        <div className="col-6 col-md-3">
                            <label>المحافظة <span className="text-danger">*</span></label>
                            <select
                                className="form-select"
                                value={filters.governorate}
                                disabled={!isSuperAdmin}
                                onChange={(e) => updateFilter("governorate", e.target.value)}
                                required
                            >
                                <option value="">اختر المحافظة</option>
                                {availableGovernorates.map((g) => (
                                    <option key={g} value={g}>{g}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-6 col-md-3">
                            <button
                                type="button"
                                className="btn btn-primary w-100 fw-bold"
                                onClick={fetchReport}
                                disabled={loading || !filters.from || !filters.to || !filters.governorate}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <>
                                        <FaSearch className="ms-1" /> عرض التقرير
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-danger rounded-4 shadow-sm">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                </div>
            )}

            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-grow text-primary" />
                    <p className="mt-3 text-muted mb-0">جاري تحميل مصفوفة نشاط الدوريات...</p>
                </div>
            )}

            {!loading && matrixView && (
                <>
                    <div className="pm-summary mb-3">
                        <div className="pm-summary__card pm-summary__card--est">
                            <div className="pm-summary__label"><FaBuilding /> فعاليات</div>
                            <div className="pm-summary__value">{matrixView.grand.establishments}</div>
                        </div>
                        <div className="pm-summary__card pm-summary__card--visit">
                            <div className="pm-summary__label"><FaClipboardCheck /> زيارات</div>
                            <div className="pm-summary__value">{matrixView.grand.inspectionVisits}</div>
                        </div>
                        <div className="pm-summary__card pm-summary__card--viol">
                            <div className="pm-summary__label"><FaExclamationTriangle /> ضبوط</div>
                            <div className="pm-summary__value">{matrixView.grand.violationReports}</div>
                        </div>
                        <div className="pm-summary__card pm-summary__card--users">
                            <div className="pm-summary__label"><FaUserShield /> دوريات نشطة</div>
                            <div className="pm-summary__value">{matrixView.grand.active}</div>
                        </div>
                    </div>

                    <div className="patrol-matrix-legend mb-2">
                        <span><FaBuilding className="text-primary" /> فعاليات</span>
                        <span><FaClipboardCheck className="text-success" /> زيارات</span>
                        <span><FaExclamationTriangle className="text-danger" /> ضبوط</span>
                        <span className="text-muted">اضغط على التاريخ لعرض مخطط اليوم</span>
                        {useCompactCells && (
                            <span className="text-muted">العرض المختصر: فعاليات / زيارات / ضبوط</span>
                        )}
                    </div>

                    <div className="ps-matrix-table-wrap">
                        {useScrollLayout && (
                            <div className="ps-matrix-scroll-hint px-3 pt-2 small text-muted">
                                اسحب أفقياً لعرض المزيد من المستخدمين — عمود الإجمالي ثابت
                            </div>
                        )}
                        <div
                            className={`ps-matrix-scroll ${
                                useScrollLayout ? "ps-matrix-scroll--wide" : ""
                            }`}
                        >
                            <table
                                className={`ps-matrix-table patrol-matrix-table ${
                                    useScrollLayout ? "ps-matrix-table--scroll" : ""
                                } ${useCompactCells ? "patrol-matrix-table--compact" : ""}`}
                            >
                                <thead>
                                    <tr>
                                        <th className="patrol-matrix-th-day">
                                            <FaCalendarAlt className="ms-1" /> اليوم
                                        </th>
                                        {matrixView.users.map((u) => (
                                            <th
                                                key={u.userId}
                                                className="patrol-matrix-th-user"
                                                title={u.name}
                                            >
                                                <div className="patrol-matrix-user-head">
                                                    <div className="patrol-matrix-user-head__name">
                                                        {shortName(u.name)}
                                                    </div>
                                                    {u.governorate && (
                                                        <div className="patrol-matrix-user-head__meta">
                                                            {u.governorate}
                                                        </div>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                        <th className="patrol-matrix-th-total">إجمالي اليوم</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {matrixView.dayRows.map((row) => (
                                        <tr key={row.day}>
                                            <td className="patrol-matrix-day-cell text-end">
                                                <button
                                                    type="button"
                                                    className="patrol-matrix-day-btn"
                                                    onClick={() => openDayChart(row)}
                                                    title="عرض مخطط نشاط هذا اليوم"
                                                >
                                                    <div className="patrol-matrix-day-label">
                                                        {formatDateLabel(row.day)}
                                                    </div>
                                                    <div className="patrol-matrix-day-iso">{row.day}</div>
                                                    <span className="patrol-matrix-day-chart-hint">
                                                        <FaChartBar /> مخطط
                                                    </span>
                                                </button>
                                            </td>
                                            {row.cells.map((cell, idx) => {
                                                const user = matrixView.users[idx];
                                                const hasData = !!cell?.totals;
                                                return (
                                                    <td
                                                        key={`${row.day}-${user.userId}`}
                                                        className={
                                                            hasData
                                                                ? "patrol-matrix-active-cell"
                                                                : "patrol-matrix-empty-cell text-center"
                                                        }
                                                        title={
                                                            hasData
                                                                ? `${user.name} — ${row.day}`
                                                                : "لا توجد دورية"
                                                        }
                                                    >
                                                        {hasData ? (
                                                            <CellTotals
                                                                totals={cell.totals}
                                                                compact={useCompactCells}
                                                            />
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td className="patrol-matrix-day-total-cell">
                                                <CellTotals
                                                    totals={row.dayTotals}
                                                    compact={useCompactCells}
                                                />
                                                <div className="patrol-matrix-mini">
                                                    {row.dayTotals.active} دورية
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="patrol-matrix-tf-label">إجمالي المستخدم</td>
                                        {matrixView.userTotals.map((t, idx) => (
                                            <td
                                                key={`ut-${matrixView.users[idx].userId}`}
                                                className="patrol-matrix-user-total-cell"
                                            >
                                                <CellTotals totals={t} compact={useCompactCells} />
                                                <div className="patrol-matrix-mini">{t.active} يوم</div>
                                            </td>
                                        ))}
                                        <td className="patrol-matrix-grand-cell">
                                            <CellTotals
                                                totals={matrixView.grand}
                                                compact={useCompactCells}
                                            />
                                            <div className="patrol-matrix-mini">الإجمالي</div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {!loading && !matrixData && !error && (
                <div className="ps-matrix-empty text-center py-5 bg-white rounded-4 shadow-sm">
                    <FaTable className="display-4 text-muted opacity-25 mb-3" />
                    <h5 className="text-dark">اختر الفترة والمحافظة ثم اعرض التقرير</h5>
                    <p className="text-muted mb-0">
                        الصفوف = الأيام · الأعمدة = المستخدمون · القيم = فعاليات / زيارات / ضبوط
                    </p>
                </div>
            )}

            {!loading && matrixData && matrixView && matrixView.users.length === 0 && (
                <div className="ps-matrix-empty text-center py-5 bg-white rounded-4 shadow-sm">
                    <h5 className="text-dark">لا توجد بيانات مطابقة</h5>
                    <p className="text-muted mb-0">جرّب تغيير الفترة أو المحافظة.</p>
                </div>
            )}

            {dayChart && (
                <div
                    className="pm-chart-overlay"
                    onClick={() => setDayChart(null)}
                    role="presentation"
                >
                    <div
                        className="pm-chart-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="pm-chart-modal__header">
                            <FaChartBar className="text-primary" />
                            <div className="flex-grow-1">
                                <h5 className="mb-0 fw-bold">نشاط الدوريات</h5>
                                <small className="text-muted">
                                    {formatDateLabel(dayChart.day)} — {dayChart.day}
                                    {filters.governorate ? ` · ${filters.governorate}` : ""}
                                </small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setDayChart(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="pm-chart-modal__filters">
                            {METRIC_OPTIONS.map((opt) => {
                                const Icon = opt.icon;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        className={`pm-chart-metric-btn ${
                                            chartMetric === opt.key ? "is-active" : ""
                                        }`}
                                        onClick={() => setChartMetric(opt.key)}
                                    >
                                        <Icon />
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="pm-chart-modal__summary">
                            <span>
                                <FaBuilding className="text-primary" />
                                {dayChart.dayTotals.establishments} منشأة
                            </span>
                            <span>
                                <FaClipboardCheck className="text-success" />
                                {dayChart.dayTotals.inspectionVisits} زيارة
                            </span>
                            <span>
                                <FaExclamationTriangle className="text-danger" />
                                {dayChart.dayTotals.violationReports} ضبط
                            </span>
                            <span>
                                <FaUserShield />
                                {dayChart.dayTotals.active} دورية
                            </span>
                        </div>

                        {chartStats && (
                            <div className="pm-chart-stats">
                                <div className="pm-chart-stats__card pm-chart-stats__card--max">
                                    <div className="pm-chart-stats__label">الأكبر</div>
                                    <div className="pm-chart-stats__value">{chartStats.max}</div>
                                    <div className="pm-chart-stats__name" title={chartStats.maxName}>
                                        {chartStats.maxName}
                                    </div>
                                </div>
                                <div className="pm-chart-stats__card pm-chart-stats__card--min">
                                    <div className="pm-chart-stats__label">الأصغر</div>
                                    <div className="pm-chart-stats__value">{chartStats.min}</div>
                                    <div className="pm-chart-stats__name" title={chartStats.minName}>
                                        {chartStats.minName}
                                    </div>
                                </div>
                                <div className="pm-chart-stats__card pm-chart-stats__card--avg">
                                    <div className="pm-chart-stats__label">المتوسط</div>
                                    <div className="pm-chart-stats__value">{chartStats.avg}</div>
                                    <div className="pm-chart-stats__name">
                                        حسب {selectedMetric?.label || "المؤشر"}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="pm-chart-modal__body" dir="ltr">
                            {chartData.length === 0 ? (
                                <div className="text-center text-muted py-5" dir="rtl">
                                    لا توجد قيم أكبر من صفر لهذا المؤشر في هذا اليوم
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={chartHeight}>
                                    <BarChart
                                        data={chartData}
                                        layout="vertical"
                                        margin={{ top: 12, right: 28, left: 8, bottom: 12 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                                        <XAxis
                                            type="number"
                                            allowDecimals={false}
                                            tick={{ fontSize: 12, fill: "#64748b" }}
                                            axisLine={{ stroke: "#cbd5e1" }}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="name"
                                            width={168}
                                            orientation="left"
                                            interval={0}
                                            tick={<ChartYTick />}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <Tooltip content={<DayChartTooltip />} />
                                        {chartMetric === "all" && <Legend />}
                                        {chartMetric === "all" ? (
                                            <>
                                                <Bar
                                                    dataKey="establishments"
                                                    name="منشآت"
                                                    fill="#2563eb"
                                                    radius={[0, 4, 4, 0]}
                                                    maxBarSize={18}
                                                />
                                                <Bar
                                                    dataKey="inspectionVisits"
                                                    name="زيارات"
                                                    fill="#16a34a"
                                                    radius={[0, 4, 4, 0]}
                                                    maxBarSize={18}
                                                />
                                                <Bar
                                                    dataKey="violationReports"
                                                    name="ضبوط"
                                                    fill="#dc2626"
                                                    radius={[0, 4, 4, 0]}
                                                    maxBarSize={18}
                                                />
                                            </>
                                        ) : (
                                            <Bar
                                                dataKey={selectedMetric.dataKey}
                                                name={selectedMetric.label}
                                                fill={selectedMetric.color}
                                                radius={[0, 6, 6, 0]}
                                                maxBarSize={28}
                                                label={{
                                                    position: "right",
                                                    fill: "#334155",
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {chartData.map((entry) => (
                                                    <Cell
                                                        key={entry.fullName}
                                                        fill={selectedMetric.color}
                                                        fillOpacity={
                                                            entry[selectedMetric.dataKey] > 0 ? 1 : 0.25
                                                        }
                                                    />
                                                ))}
                                            </Bar>
                                        )}
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
